import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EnvironmentInjector,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { Editor, Extension } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Subscription } from "rxjs";
import { ConfigService } from "src/app/services/config.service";
import { createUserMention, isMentionListOpen, MentionableUser } from "../spark-editor-tiptap/mention-extension";

@Component({
  selector: "app-answer-editor",
  template: `<div #host class="answer-editor" [class.disabled]="disabled"></div>`,
  styleUrls: ["./answer-editor.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnswerEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() content = "";
  @Input() placeholder = "";
  @Input() disabled = false;
  @Input() autofocus = false;
  @Output() submitted = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild("host", { static: true }) host: ElementRef<HTMLElement>;

  private editor: Editor;
  private mentionableUsers: MentionableUser[] = [];
  private userListSubscription: Subscription;

  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  constructor(private configService: ConfigService) {}

  ngOnInit(): void {
    this.userListSubscription = this.configService.userList$.subscribe(
      (users) => (this.mentionableUsers = users ?? []),
    );
    this.configService.loadUserList();

    this.editor = new Editor({
      element: this.host.nativeElement,
      content: this.content || "",
      editable: !this.disabled,
      autofocus: this.autofocus ? "end" : false,
      extensions: [
        StarterKit.configure({ heading: false, codeBlock: false, horizontalRule: false }),
        Placeholder.configure({ placeholder: this.placeholder }),
        createUserMention(() => this.mentionableUsers, this.appRef, this.injector),
        this.submitShortcuts(),
      ],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["disabled"] && this.editor) {
      this.editor.setEditable(!this.disabled);
    }
  }

  ngOnDestroy(): void {
    this.userListSubscription?.unsubscribe();
    this.editor?.destroy();
  }

  clear(): void {
    this.editor?.commands.clearContent();
  }

  private submitShortcuts() {
    const component = this;

    return Extension.create({
      name: "answerSubmit",
      priority: 1000,
      addKeyboardShortcuts() {
        return {
          // Enter must reach the mention list first while it is open, to pick the highlighted user.
          Enter: ({ editor }) => {
            if (isMentionListOpen(editor)) return false;
            if (!editor.getText().trim()) return true;
            component.submitted.emit(editor.getHTML());
            return true;
          },
          Escape: ({ editor }) => {
            if (isMentionListOpen(editor)) return false;
            component.cancelled.emit();
            return true;
          },
        };
      },
    });
  }
}
